import { forwardRef } from 'react';
import { ChevronDown } from 'mdi-material-ui';
import { styled } from '../../utils';
import { CollapsibleIconBaseProps } from './model';

export interface CollapsibleIconProps extends Pick<CollapsibleIconBaseProps, 'collapsed'> {}

/**
 * Use the `CollapsibleIcon` component when you are presenting the user with the ability to expand/collapse content
 * vertically, and the collapse icon is part of another component that manages focus, clicking, and accessible labels
 * (e.g. filter sidebars in logging and changes explorer use `CollapsibleIcon` inside a `ListItemButton` that manages
 * focus, clicking, and accessible labels).
 *
 * If the collapse icon is not part of a larger component that manages these patterns, use `CollapsibleIconButton`.
 *
 * The `collapsed` behavior of this component is controlled (i.e. the consumer is responsible for managing the state).
 *
 * When building components that manage expand/collapse state, you may want to use the `useCollapsibleA11yProps` hook to
 * ensure consistent accessible props.
 */
export const CollapsibleIcon = forwardRef<SVGSVGElement, CollapsibleIconProps>(function CollapsibleIcon(
  { collapsed },
  ref
) {
  return <CollapsibleChevronDown ref={ref} collapsed={collapsed} />;
});

const CollapsibleChevronDown = styled(ChevronDown, {
  // Need to remove collapsed or it causes the browser to throw errors because it gets passed to the svg icon, which
  // doesn't know how to handle it.
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<Pick<CollapsibleIconProps, 'collapsed'>>(({ collapsed, theme }) => {
  return {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    transition: 'all 200ms ease',
    transform: collapsed ? `rotate(-90deg)` : '',
    width: '20px',
    height: '20px',
  };
});
